// src/components/home/DatabaseUpdates.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DocumentSnapshot } from 'firebase/firestore';
import { DatabaseUpdate, UpdateCategory, UPDATE_CATEGORIES, isFlaggedNew, toDate } from '../../types/updates';
import { getUpdatesForPublic } from '../../services/UpdatesService';
import { useAuth } from '../../contexts/AuthContext';
import { markNewsSeen, readNewsSeenAt } from '../../services/NewsSeenService';

const BATCH_SIZE = 5;
type Filter = UpdateCategory | 'all';

/**
 * The changelog, as a deck of slides.
 *
 * It used to render five entries stacked, and a single entry can run to 900px, so
 * the section became several thousand pixels of scrolling. One entry is shown at a
 * time instead.
 *
 * Entries already fetched are kept, and moving between them is local — the previous
 * version re-queried Firestore every time the reader paged, including paging
 * backwards to something it had just downloaded. A new read now happens only when
 * the reader reaches the end of what has been loaded and more exists.
 */
export const DatabaseUpdates: React.FC = () => {
    const { currentUser } = useAuth();
    const [updates, setUpdates] = useState<DatabaseUpdate[]>([]);
    const [index, setIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [filter, setFilter] = useState<Filter>('all');
    /** Taken once on arrival: the marker is stamped forward immediately, so reading it later
     *  would always say "nothing new". */
    const [seenAt, setSeenAt] = useState<Date | null>(null);

    const cursorRef = useRef<DocumentSnapshot | undefined>(undefined);
    const loadingRef = useRef(false);

    /**
     * Fetches a page. `restart` throws away the cursor and the deck, which is what changing the
     * filter needs — it is a different query, so the old cursor points into the wrong result
     * set and the old entries do not belong to it.
     *
     * The reset happens HERE rather than in its own effect on `filter`. As two effects it was a
     * race: both fire on the same render, the fetch resolved first and the reset then wiped the
     * results, so every filter read as empty.
     */
    const loadMore = useCallback(async (restart = false) => {
        // Guards against a double-fetch when the reader clicks through quickly.
        if (loadingRef.current) return;
        loadingRef.current = true;
        setLoading(true);
        setError(null);
        if (restart) cursorRef.current = undefined;

        try {
            const result = await getUpdatesForPublic(BATCH_SIZE, cursorRef.current, filter);
            cursorRef.current = result.lastDoc ?? undefined;
            setHasMore(result.hasMore);
            setUpdates((prev) => {
                const base = restart ? [] : prev;
                const seen = new Set(base.map((u) => u.id));
                return [...base, ...result.updates.filter((u) => !seen.has(u.id))];
            });
            if (restart) setIndex(0);
        } catch (err: any) {
            console.error('Error loading updates:', err);
            setError('Viga uuenduste laadimisel');
        } finally {
            loadingRef.current = false;
            setLoading(false);
        }
    }, [filter]);

    // One effect, and it restarts: `loadMore` is rebuilt whenever the filter changes, so this
    // fires exactly when a fresh query is needed and never leaves a stale cursor behind.
    useEffect(() => {
        loadMore(true);
    }, [loadMore]);

    // How much has appeared since this account last looked. Read before marking, and marked
    // straight away: a visitor who scrolled past the section has still seen that it is there.
    useEffect(() => {
        if (!currentUser) return;
        let cancelled = false;
        readNewsSeenAt(currentUser.uid).then((at) => {
            if (cancelled) return;
            setSeenAt(at);
            markNewsSeen(currentUser.uid);
        });
        return () => { cancelled = true; };
    }, [currentUser]);

    const atEnd = index >= updates.length - 1;

    const goNext = useCallback(() => {
        if (!atEnd) {
            setIndex((i) => i + 1);
        } else if (hasMore) {
            // Fetch the next batch and step onto its first entry.
            loadMore().then(() => setIndex((i) => i + 1));
        }
    }, [atEnd, hasMore, loadMore]);

    const goPrev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'ArrowRight') {
            event.preventDefault();
            goNext();
        } else if (event.key === 'ArrowLeft') {
            event.preventDefault();
            goPrev();
        }
    };

    const formatDate = (date: any) => {
        let dateObj: Date;

        if (date?.toDate) {
            dateObj = date.toDate();
        } else if (date instanceof Date) {
            dateObj = date;
        } else {
            return '—';
        }

        return dateObj.toLocaleDateString('et-EE');
    };

    /** Counted from the batch already in hand rather than with another query: the first page
     *  is five items, and "5+" is as much as this line ever needs to say. */
    const newSince = seenAt
        ? updates.filter((u) => {
            const at = toDate(u.publishedAt);
            return at !== null && at.getTime() > seenAt.getTime();
        }).length
        : 0;

    const renderBody = () => {
        if (loading && updates.length === 0) {
            return <p className="lp-log__state">Laadin uuendusi…</p>;
        }

        if (error && updates.length === 0) {
            return (
                <div className="lp-log__state">
                    <p style={{ margin: '0 0 1rem' }}>{error}</p>
                    <button type="button" className="lp-log__btn" onClick={() => loadMore(true)} style={{ width: 'auto', padding: '0 1rem' }}>
                        Proovi uuesti
                    </button>
                </div>
            );
        }

        if (updates.length === 0) {
            return <p className="lp-log__state">Uuendusi ei ole veel lisatud.</p>;
        }

        const current = updates[Math.min(index, updates.length - 1)];

        return (
            <>
                <div
                    className="lp-log__deck"
                    tabIndex={0}
                    role="group"
                    aria-roledescription="karussell"
                    aria-label="Mängu uuendused"
                    onKeyDown={onKeyDown}
                >
                    {/* Keyed by id so React remounts on change and the entrance runs. */}
                    <article
                        className="lp-log__slide"
                        key={current.id || index}
                        aria-live="polite"
                    >
                        <div className="lp-log__head">
                            <div className="lp-log__marks">
                                <time className="lp-log__date">{formatDate(current.publishedAt)}</time>
                                {(() => {
                                    const cat = UPDATE_CATEGORIES.find((c) => c.id === current.category);
                                    return cat ? (
                                        <span className={`lp-log__cat lp-log__cat--${current.category}`}>
                                            {cat.label}
                                        </span>
                                    ) : null;
                                })()}
                                {isFlaggedNew(current) && <span className="lp-log__new">UUS</span>}
                            </div>
                            <h3 className="lp-log__title">{current.title}</h3>
                        </div>
                        {/* Authored in the admin panel, which allows formatting markup.
                            Writes to `updates` are admin-only in firestore.rules. */}
                        <div
                            className="lp-log__body"
                            dangerouslySetInnerHTML={{ __html: current.content }}
                        />
                    </article>
                </div>

                <div className="lp-log__nav">
                    <div className="lp-log__dots">
                        {updates.map((update, i) => (
                            <button
                                type="button"
                                key={update.id || i}
                                className={`lp-log__dot${i === index ? ' lp-log__dot--on' : ''}`}
                                onClick={() => setIndex(i)}
                                aria-label={`Uuendus ${i + 1}`}
                                aria-current={i === index}
                            />
                        ))}
                    </div>

                    <div className="lp-log__arrows">
                        <button
                            type="button"
                            className="lp-log__btn"
                            onClick={goPrev}
                            disabled={index === 0}
                            aria-label="Eelmine uuendus"
                        >
                            ‹
                        </button>
                        <span className="lp-log__count">
                            {index + 1} / {updates.length}
                            {hasMore ? '+' : ''}
                        </span>
                        <button
                            type="button"
                            className="lp-log__btn"
                            onClick={goNext}
                            disabled={(atEnd && !hasMore) || loading}
                            aria-label="Järgmine uuendus"
                        >
                            ›
                        </button>
                    </div>
                </div>
            </>
        );
    };

    return (
        <section className="lp-section lp-log" id="uuendused">
            <div className="lp-shell">
                <div className="lp-section__head">
                    <p className="lp-eyebrow">
                        <span className="lp-eyebrow__dot" />
                        Päevik
                    </p>
                    <h2 className="lp-section__title">Uuendused</h2>
                </div>

                <div className="lp-log__bar">
                    <div className="lp-log__filters" role="group" aria-label="Uuenduste liik">
                        <button
                            type="button"
                            className={`lp-log__filter${filter === 'all' ? ' lp-log__filter--on' : ''}`}
                            onClick={() => setFilter('all')}
                        >
                            Kõik
                        </button>
                        {UPDATE_CATEGORIES.map((cat) => (
                            <button
                                type="button"
                                key={cat.id}
                                className={`lp-log__filter lp-log__filter--${cat.id}${filter === cat.id ? ' lp-log__filter--on' : ''}`}
                                onClick={() => setFilter(cat.id)}
                                title={cat.hint}
                            >
                                {cat.label}
                            </button>
                        ))}
                    </div>

                    {newSince > 0 && (
                        <p className="lp-log__since">
                            <span className="lp-log__sinceCount">{newSince}</span>
                            {newSince === 1 ? ' uus alates su viimasest külastusest' : ' uut alates su viimasest külastusest'}
                        </p>
                    )}
                </div>

                {renderBody()}
            </div>
        </section>
    );
};
